/**
 * A condition while it is being edited in the advanced filter sheet. It lives apart from
 * `ConditionRow.tsx`, which exports its component only (react-refresh/only-export-components).
 */
import type { Condition, ConditionValue, Register } from '../query/types'
import { isValidValue } from '../query/validate'

/** A condition as it is being edited: it may lack a property, an operator or a (good) value. */
export interface DraftCondition {
  property: string | null
  operator: string | null
  value?: ConditionValue
}

/** The draft of a condition that is already in a filter. */
export function toDraft(condition: Condition): DraftCondition {
  return { property: condition.property, operator: condition.operator, value: condition.value }
}

/**
 * The condition a draft makes, or null while it is not one: it needs a property and an operator of
 * the register and, when the operator takes one, a value of the right shape (`isValidValue`, the
 * check a link goes through too). Text is trimmed; an operator that takes no value carries none.
 */
export function completeCondition(draft: DraftCondition, register: Register): Condition | null {
  const property = register.properties.find((candidate) => candidate.key === draft.property)
  const operator = property?.operators.find((candidate) => candidate.key === draft.operator)
  if (!property || !operator) return null
  if (operator.value === 'none') return { property: property.key, operator: operator.key }
  if (draft.value === undefined || !isValidValue(operator.value, draft.value)) return null
  const value = operator.value === 'text' && typeof draft.value === 'string' ? draft.value.trim() : draft.value
  return { property: property.key, operator: operator.key, value }
}
